import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, useTheme, Typography, useMediaQuery, CircularProgress } from '@mui/material';

import {
  MapContainer,
  BindingsPanel,
  OkkoStationsTable,
  parseDistanceForApi,
  CompetitorStationsTable
} from './components';

import { useAppSelector } from '../../store';
import { useRolePermissions } from '@/hooks/useRolePermissions';

import { Binding, SelectedFilters, Station, isStationWithUnicode, BulkUpdateError } from '@/types/azk-binding.types';

import './AzkBindingPage.css';
import {
  useBulkUpdateBindingsMutation,
  useDeleteBindingMutation,
  useGetBindingsForCompetitorQuery,
  useGetBindingsForOkkoQuery,
  useGetCompetitorFiltersQuery,
  useGetCompetitorStationsQuery,
  useGetFormOptionsQuery,
  useGetOkkoStationsQuery
} from '@/services/azk-binding.api';
import {
  buildBulkRequest,
  defaultSelectedFilters,
  getCleanedFilters,
  getHasChanges,
  getNextFilters,
  getNextItemsAfterAdd,
  getNextItemsAfterDeletion,
  getNextUpdatedItems,
  getOkkoBindingDetails,
  getOkkoBindingsForCompetitor2,
  sortStations
} from './azk-binging.utils';
import { useDebounce, useSnackbar } from '@/hooks';

/**
 * Main page component for AZK (gas station) binding management
 * Allows users to create and manage relationships between OKKO stations and competitor stations
 *
 * Features:
 * - Interactive map visualization of station relationships
 * - Station search and filtering capabilities
 * - Binding creation, editing, and deletion
 * - Real-time validation and change tracking
 *
 
 */
function AzkBindingPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const lastAppliedOkkoBindingsTsRef = useRef<number | undefined>(undefined);

  const isAuthorized = useAppSelector(state => state.global.isAuthorized);
  const { canEditBindings } = useRolePermissions();
  const readOnly = !canEditBindings;
  const { setSnackbarMessage } = useSnackbar();
  const [mapInitialized, setMapInitialized] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>(defaultSelectedFilters);
  const [competitorSearchQuery, setCompetitorSearchQuery] = useState('');
  const [okkoSearchQuery, setOkkoSearchQuery] = useState('');
  const [okkoSortField, setOkkoSortField] = useState('station_number');
  const [okkoSortDirection, setOkkoSortDirection] = useState<'asc' | 'desc'>('asc');
  const [competitorSortField, setCompetitorSortField] = useState('station_number');
  const [competitorSortDirection, setCompetitorSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedOkko, setSelectedOkko] = useState<Station | null>(null);
  const [selectedCompetitor, setSelectedCompetitor] = useState<Station | null>(null);
  const [bindingsOkko, setBindingsOkko] = useState<Binding[]>([]);
  const [addingMode, setAddingMode] = useState(false);
  const [resetting, setResetting] = useState(false);

  const requestParams = {
    skip: !isAuthorized,
    refetchOnMountOrArgChange: true
  };

  const {
    data: options,
    isLoading: optionsLoading,
    isError: optionsError
  } = useGetFormOptionsQuery(undefined, requestParams);

  const {
    data: competitorFilters = { regions: [], province: [], brands: [] },
    isLoading: competitorFiltersLoading,
    isError: competitorFiltersError
  } = useGetCompetitorFiltersQuery(getCleanedFilters(selectedFilters), requestParams);

  const debouncedCompetitorSearch2 = useDebounce(competitorSearchQuery, 500);
  const debouncedOkkoSearch2 = useDebounce(okkoSearchQuery, 500);

  const { data: allCompetitorStations = [], isLoading: allCompetitorStationsLoading } = useGetCompetitorStationsQuery(
    {},
    requestParams
  );

  const { data: allOkkoStations = [] } = useGetOkkoStationsQuery({ search: '' }, requestParams);

  const {
    data: competitorStations = [],
    isLoading: competitorsLoading,
    isError: competitorsError
  } = useGetCompetitorStationsQuery(
    { ...getCleanedFilters(selectedFilters), search: debouncedCompetitorSearch2 },
    requestParams
  );

  const {
    data: okkoStations = [],
    isLoading: okkoStationsLoading,
    isError: okkoStationsError
  } = useGetOkkoStationsQuery({ search: debouncedOkkoSearch2 }, requestParams);

  const isDataLoading =
    optionsLoading ||
    competitorFiltersLoading ||
    competitorsLoading ||
    okkoStationsLoading ||
    allCompetitorStationsLoading;

  const requestBindingParams = {
    skip:
      !isAuthorized ||
      !selectedCompetitor ||
      !isStationWithUnicode(selectedCompetitor) ||
      !selectedCompetitor.unicode ||
      isDataLoading,
    refetchOnMountOrArgChange: true
  };

  const requestOkkoBindingParams = {
    skip:
      !isAuthorized || !selectedOkko || !isStationWithUnicode(selectedOkko) || !selectedOkko.unicode || isDataLoading,
    refetchOnMountOrArgChange: true
  };

  const {
    data: competitorBindings = [],
    // isLoading: competitorBindingsLoading,
    isError: competitorBindingsError
  } = useGetBindingsForCompetitorQuery(
    { competitorStationUnicode: String(selectedCompetitor?.unicode ?? '') },
    requestBindingParams
  );

  const {
    data: okkoBindings,
    // isLoading: okkoBindingsLoading,
    isSuccess: okkoBindingsSuccess,
    fulfilledTimeStamp: okkoBindingsTs,
    isError: okkoBindingsError
  } = useGetBindingsForOkkoQuery({ okkoStationUnicode: String(selectedOkko?.unicode ?? '') }, requestOkkoBindingParams);

  const [bulkUpdateBindingsMutation] = useBulkUpdateBindingsMutation();
  const [deleteBindingMutation] = useDeleteBindingMutation();

  const influenceTypes = options?.influence_types || [];
  const locationTypes = options?.location_types || [];

  const handleOnFilterChange = (filterType: keyof SelectedFilters, value: string) => {
    const nextFilters = getNextFilters(selectedFilters, filterType, value);
    setSelectedFilters(nextFilters);
  };

  useEffect(() => {
    if (optionsError || competitorFiltersError || competitorsError || okkoStationsError) {
      setSnackbarMessage('Помилка завантаження даних', 'error');
    }
  }, [optionsError, competitorFiltersError, competitorsError, okkoStationsError, setSnackbarMessage]);

  useEffect(() => {
    // Make layout visible after data is initialized
    if (!isDataLoading) {
      setTimeout(() => {
        const layout = document.querySelector('.azk-binding-main-layout');
        if (layout && !layout.classList.contains('layout-ready')) {
          layout.classList.add('layout-ready');
        }
      }, 100);
    }
  }, [isDataLoading]);

  useEffect(() => {
    if (competitorBindingsError || okkoBindingsError) {
      setSnackbarMessage("Помилка завантаження прив'язок", 'error');
    }
  }, [competitorBindingsError, okkoBindingsError, setSnackbarMessage]);

  useEffect(() => {
    if (!selectedOkko || !okkoBindingsSuccess || !okkoBindings) return;
    if (lastAppliedOkkoBindingsTsRef.current === okkoBindingsTs) return;

    lastAppliedOkkoBindingsTsRef.current = okkoBindingsTs;

    setBindingsOkko(okkoBindings.map(binding => ({ ...binding, status: 'unchanged' })));
  }, [okkoBindings, okkoBindingsSuccess, okkoBindingsTs, selectedOkko]);

  const okkoBindingsForCompetitor = useMemo(
    () => getOkkoBindingsForCompetitor2(selectedCompetitor, competitorBindings),
    [selectedCompetitor, competitorBindings, okkoStations]
  );

  const okkoBindingDetails = useMemo(
    () => getOkkoBindingDetails(selectedOkko, bindingsOkko, allCompetitorStations),
    [selectedOkko, bindingsOkko, allCompetitorStations]
  );

  const hasChanges = useMemo(() => getHasChanges(bindingsOkko), [bindingsOkko]);

  const deletedBindingsId = useMemo(
    () => bindingsOkko.filter(b => b.status === 'deleted').map(b => b.id),
    [bindingsOkko]
  );

  const sortedOkkoStations = useMemo(
    () => sortStations(okkoStations, okkoSortField, okkoSortDirection),
    [okkoStations, okkoSortField, okkoSortDirection]
  );

  const sortedCompetitorStations = useMemo(
    () => sortStations(competitorStations, competitorSortField, competitorSortDirection),
    [competitorStations, competitorSortField, competitorSortDirection]
  );

  const onSort = (column: string) => {
    if (okkoSortField === column) {
      setOkkoSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOkkoSortField(column);
      setOkkoSortDirection('asc');
    }
  };

  const onCompetitorSort = (column: string) => {
    if (competitorSortField === column) {
      setCompetitorSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setCompetitorSortField(column);
      setCompetitorSortDirection('asc');
    }
  };

  const selectOkkoStation = (station: Station) => {
    if (isDataLoading || !isStationWithUnicode(station) || selectedOkko?.unicode === station.unicode) return;
    setSelectedOkko(station);
    setSelectedCompetitor(null);
    setAddingMode(false);
  };

  const selectCompetitorStation = (station: Station) => {
    if (isDataLoading || !isStationWithUnicode(station) || selectedCompetitor?.unicode === station.unicode) return;
    setSelectedCompetitor(station);
    setSelectedOkko(null);
    setAddingMode(false);
    setBindingsOkko([]);
  };

  const resetChanges = () => {
    setResetting(true);

    setTimeout(() => {
      const isStationContext = addingMode || Boolean(selectedOkko);

      if (isStationContext && okkoBindings) {
        setBindingsOkko(okkoBindings.map(binding => ({ ...binding, status: 'unchanged' })));
        setAddingMode(false);
        setResetting(false);

        setSnackbarMessage(addingMode ? 'Режим додавання скасовано' : 'Зміни скасовано', 'success');

        return;
      }

      setSelectedCompetitor(null);
      setSelectedOkko(null);
      setAddingMode(false);

      setSnackbarMessage('Повернуто до початкового вигляду', 'success');
    }, 300);
  };

  const saveAllChanges = async () => {
    if (!selectedOkko || !isStationWithUnicode(selectedOkko)) {
      setSnackbarMessage('Оберіть АЗС ОККО для збереження', 'error');
      return;
    }

    const bindingsToDelete = bindingsOkko.filter(binding => binding.status === 'deleted' && binding.type !== 'new');
    const remainingBindings = bindingsOkko.filter(binding => binding.status !== 'deleted');

    if (bindingsToDelete.length === 0 && remainingBindings.length === 0) {
      setSnackbarMessage('Немає змін для збереження', 'success');
      return;
    }

    try {
      if (bindingsToDelete.length > 0) {
        await Promise.all(bindingsToDelete.map(binding => deleteBindingMutation(Number(binding.id)).unwrap()));
      }

      if (remainingBindings.length > 0) {
        const bulkRequest = buildBulkRequest(Number(selectedOkko.unicode), remainingBindings, parseDistanceForApi, influenceTypes, locationTypes);
        const bulkResult = await bulkUpdateBindingsMutation(bulkRequest).unwrap();

        if (bulkResult?.errors?.length > 0) {
          setSnackbarMessage(
            `Помилки збереження: ${bulkResult.errors.map((e: BulkUpdateError) => e.error).join(', ')}`,
            'error'
          );
        } else {
          setSnackbarMessage('Зміни збережено', 'success');
        }
      } else {
        setSnackbarMessage('Зміни збережено', 'success');
      }

      setAddingMode(false);
    } catch (error: unknown) {
      setSnackbarMessage('Помилка збереження змін', 'error');
    }
  };

  const addCompetitorBinding = (competitor: Station) => {
    if (!selectedOkko || !addingMode || !selectedOkko?.unicode) return;

    const { nextItems, alreadyExists } = getNextItemsAfterAdd(bindingsOkko, selectedOkko, competitor);

    if (alreadyExists) {
      setSnackbarMessage("Ця АЗК вже прив'язана", 'error');
      return;
    }
    if (!nextItems) return;

    setBindingsOkko(nextItems);

    // Trigger map update to show new marker
    setTimeout(() => {
      if (window.updateMapMarkers) {
        window.updateMapMarkers();
      }
    }, 100);
  };

  const updateBindingField = (bindingId: number, field: string, value: any) => {
    const nextItems = getNextUpdatedItems(bindingsOkko, okkoBindings, bindingId, field, value);
    if (!nextItems) return;

    setBindingsOkko(nextItems);
  };

  const markBindingForDeletion = (bindingId: number) => {
    const nextItems = getNextItemsAfterDeletion(bindingsOkko, bindingId);

    setBindingsOkko(nextItems);
  };

  const markAllBindingsForDeletion = () => {
    const nextBindings = bindingsOkko.map(binding => (binding.isOkko ? binding : { ...binding, status: 'deleted' }));
    setBindingsOkko(nextBindings);
  };

  return (
    <Box className="azk-binding-container">
      {isDataLoading ? (
        <div className="azk-binding-loading-container">
          <CircularProgress className="azk-binding-loading-spinner" />
          <Typography>Завантаження даних...</Typography>
        </div>
      ) : (
        <>
          <Box className={`azk-binding-main-layout ${isMobile ? 'mobile-layout' : ''}`}>
            <Box className={`azk-binding-station-table-container ${addingMode ? 'hidden' : ''}`}>
              <OkkoStationsTable
                searchQuery={okkoSearchQuery}
                selectedStation={selectedOkko}
                sortDirection={okkoSortDirection}
                sortField={okkoSortField}
                sortedStations={sortedOkkoStations}
                onStationSelect={selectOkkoStation}
                onSearchChange={setOkkoSearchQuery}
                onSort={onSort}
              />
            </Box>

            <MapContainer
              selectedOkkoStation={selectedOkko}
              selectedCompetitorStation={selectedCompetitor}
              bindings={selectedCompetitor ? competitorBindings : bindingsOkko}
              allCompetitorStations={allCompetitorStations}
              okkoStations={allOkkoStations}
              deletedBindings={deletedBindingsId}
              mapInitialized={mapInitialized}
              setMapInitialized={setMapInitialized}
              showError={(message: string) => setSnackbarMessage(message, 'error')}
            />
            <CompetitorStationsTable
              searchQuery={competitorSearchQuery}
              selectedStation={selectedCompetitor}
              sortField={competitorSortField}
              sortDirection={competitorSortDirection}
              selectedFilters={selectedFilters}
              filters={competitorFilters}
              addingMode={addingMode}
              sortedStations={sortedCompetitorStations}
              onSearchChange={setCompetitorSearchQuery}
              onStationSelect={selectCompetitorStation}
              onSort={onCompetitorSort}
              onFilterChange={handleOnFilterChange}
              onAddCompetitor={addCompetitorBinding}
            />
          </Box>

          <BindingsPanel
            isMobile={isMobile}
            readOnly={readOnly}
            selectedCompetitorStation={selectedCompetitor}
            selectedOkkoStation={selectedOkko}
            okkoBindingDetails={okkoBindingDetails}
            okkoBindingsForCompetitor={okkoBindingsForCompetitor}
            influenceTypes={influenceTypes}
            locationTypes={locationTypes}
            addingMode={addingMode}
            hasChanges={hasChanges}
            resetting={resetting}
            onDeleteBinding={markBindingForDeletion}
            onDeleteAll={markAllBindingsForDeletion}
            onUpdateField={updateBindingField}
            onStartAdding={() => setAddingMode(true)}
            onReset={resetChanges}
            onSave={saveAllChanges}
          />
        </>
      )}
    </Box>
  );
}

export default AzkBindingPage;
